import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/Auth/AuthContext';
import { useApp } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Camera, Heart, MapPin, List, Star, Upload, ArrowLeft, Home, Lock, Globe, UserX, Share2, Link, Check, Edit2, Tag } from 'lucide-react';
import ClickableTag from '../../components/ui/ClickableTag';
import SpotDetailsModal from '../../components/Modals/SpotDetailsModal';
import UserProfileHeader from './UserProfileHeader';

export default function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user: currentUser } = useAuth();
  const { state, actions } = useApp();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({
    likedSpots: [],
    visitedSpots: [],
    wishlists: [],
    contributions: [],
    likedPhotos: []
  });
  const [activeTab, setActiveTab] = useState('liked-spots');
  const [loadingTab, setLoadingTab] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagCategories, setTagCategories] = useState([]);
  
  // Check if this is the current user's own profile
  const isOwnProfile = isAuthenticated && currentUser && currentUser.username === username;

  useEffect(() => {
    fetchProfile();
    fetchTagData();
  }, [username]);

  const fetchTagData = async () => {
    try {
      const [tagsResponse, categoriesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags/all-from-spots`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tag-categories`)
      ]);

      if (tagsResponse.ok && categoriesResponse.ok) {
        const tags = await tagsResponse.json();
        const categories = await categoriesResponse.json();
        setAvailableTags(tags);
        setTagCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching tag data:', error);
    }
  };

  useEffect(() => {
    if (profile && !profile.is_private) {
      fetchTabData(activeTab);
    }
  }, [activeTab, profile]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile/${username}`);
      
      if (response.status === 404) {
        setError('Utilisateur non trouvé');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement du profil');
      }

      const profileData = await response.json();
      setProfile(profileData);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async (tab) => {
    setLoadingTab(true);
    try {
      let endpoint;
      let dataKey;
      let usePersonalAPI = false;
      
      switch (tab) {
        case 'liked-spots':
          endpoint = isOwnProfile ? `liked-spots` : `liked-spots`;
          dataKey = 'likedSpots';
          usePersonalAPI = isOwnProfile;
          break;
        case 'visited-spots':
          endpoint = isOwnProfile ? `visited-spots` : `visited-spots`;
          dataKey = 'visitedSpots';
          usePersonalAPI = isOwnProfile;
          break;
        case 'wishlists':
          endpoint = isOwnProfile ? `wishlists` : `wishlists`;
          dataKey = 'wishlists';
          usePersonalAPI = isOwnProfile;
          break;
        case 'contributions':
          endpoint = isOwnProfile ? `contributions` : `contributions`;
          dataKey = 'contributions';
          usePersonalAPI = isOwnProfile;
          break;
        case 'liked-photos':
          endpoint = isOwnProfile ? `liked-photos` : `liked-photos`;
          dataKey = 'likedPhotos';
          usePersonalAPI = isOwnProfile;
          break;
        default:
          return;
      }

      // Use personal API if it's own profile, otherwise use public API
      const apiUrl = usePersonalAPI 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/user/${endpoint}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/user/profile/${username}/${endpoint}`;
      
      const headers = usePersonalAPI && currentUser
        ? {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        : { 'Content-Type': 'application/json' };

      const response = await fetch(apiUrl, { headers });

      if (response.status === 403 && !isOwnProfile) {
        // Profile is private
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setProfileData(prev => ({
          ...prev,
          [dataKey]: data
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${tab}:`, error);
    } finally {
      setLoadingTab(false);
    }
  };

  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/profile/${username}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Profil de ${profile.display_name || profile.username}`,
          text: `Découvrez les spots explorés par ${profile.display_name || profile.username} sur Sun-Spot`,
          url: profileUrl
        });
      } catch (error) {
        // Fallback to clipboard if share fails
        copyProfileUrl(profileUrl);
      }
    } else {
      copyProfileUrl(profileUrl);
    }
  };

  const copyProfileUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      alert('Lien du profil copié dans le presse-papier !');
    } catch (error) {
      // Fallback: show URL in a prompt
      prompt('Copiez ce lien pour partager le profil:', url);
    }
  };

  const handleViewSpotDetails = (spot) => {
    actions.setSelectedSpot(spot);
    actions.setShowSpotDetails(true);
  };

  const getProfilePhotoUrl = () => {
    if (profile?.profile_photo) {
      return profile.profile_photo.startsWith('http') 
        ? profile.profile_photo 
        : `${process.env.REACT_APP_BACKEND_URL}${profile.profile_photo}`;
    }
    return null;
  };

  const getUserInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.substring(0, 2).toUpperCase();
    }
    if (profile?.username) {
      return profile.username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement du profil...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour
            </Button>
          </div>
          
          <div className="text-center py-12">
            <UserX className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{error}</h3>
            <p className="text-gray-500">
              L'utilisateur que vous recherchez n'existe pas ou n'est plus disponible.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
          
          <div className="flex items-center gap-2 text-gray-600">
            <Home className="h-4 w-4" />
            <span className="text-sm">
              {isOwnProfile ? 'Mon profil' : `Profil de ${profile.display_name || profile.username}`}
            </span>
          </div>
        </div>

        {/* Profile Header - Use different components based on ownership */}
        {isOwnProfile ? (
          <UserProfileHeader user={currentUser} />
        ) : (
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center space-x-6">
                {/* Profile Photo */}
                <Avatar className="h-32 w-32">
                  <AvatarImage src={getProfilePhotoUrl()} alt={profile.username} />
                  <AvatarFallback className="text-2xl">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>

                {/* Profile Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">
                      {profile.display_name || profile.username}
                    </h1>
                    {profile.is_private && (
                      <div className="flex items-center text-gray-600">
                        <Lock className="h-4 w-4 mr-1" />
                        <span className="text-sm">Privé</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-lg text-gray-600">@{profile.username}</p>
                    {profile.bio && (
                      <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
                    )}
                    {profile.location && (
                      <p className="text-gray-600 flex items-center">
                        📍 {profile.location}
                      </p>
                    )}
                    <div className="text-sm text-gray-500">
                      Membre depuis {new Date(profile.created_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </div>
                    
                    {/* Favorite Tags Display */}
                    {profile.favorite_tags && profile.favorite_tags.length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Tags préférés</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {profile.favorite_tags.map((tagSlug) => {
                            const tag = availableTags.find(t => t.slug === tagSlug);
                            const category = tag ? tagCategories.find(c => c.id === tag.category_id) : null;
                            
                            return (
                              <ClickableTag
                                key={tagSlug}
                                tag={tag || { slug: tagSlug, name: tagSlug }}
                                category={category}
                                size="xs"
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Share Button for public profiles */}
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    onClick={handleShareProfile}
                    className="flex items-center gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    Partager
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyProfileUrl(`${window.location.origin}/profile/${username}`)}
                    className="flex items-center gap-2 text-xs"
                  >
                    <Link className="h-3 w-3" />
                    Copier lien
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Header */}

        {/* Private Profile Message - only show if it's not own profile and profile is private */}
        {profile.is_private && !isOwnProfile ? (
          <Card className="mt-8">
            <CardContent className="p-8">
              <div className="text-center py-12">
                <Lock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Profil privé</h3>
                <p className="text-gray-500">
                  Ce profil est privé. Seul {profile.display_name || profile.username} peut voir son contenu.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Public Profile Content OR Own Profile */
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="liked-spots" className="flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Spots likés
              </TabsTrigger>
              <TabsTrigger value="visited-spots" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Visites
              </TabsTrigger>
              <TabsTrigger value="wishlists" className="flex items-center gap-2">
                <List className="h-4 w-4" />
                Listes d'envie
              </TabsTrigger>
              <TabsTrigger value="contributions" className="flex items-center gap-2">
                <Star className="h-4 w-4" />
                Contributions
              </TabsTrigger>
              <TabsTrigger value="liked-photos" className="flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Photos likées
              </TabsTrigger>
            </TabsList>

            <TabsContent value="liked-spots">
              <div className="mt-6">
                {loadingTab ? (
                  <div className="text-center py-8">Chargement...</div>
                ) : profileData.likedSpots.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun spot liké pour le moment</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {profileData.likedSpots.map((spot) => (
                      <Card key={spot.id} className="overflow-hidden cursor-pointer" onClick={() => handleViewSpotDetails(spot)}>
                        {spot.photos && spot.photos.length > 0 && (
                          <div className="aspect-video">
                            <img
                              src={spot.photos[0].url}
                              alt={spot.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <h3 className="font-semibold mb-2">{spot.title}</h3>
                          <p className="text-gray-600 text-sm line-clamp-2">{spot.description}</p>
                          <div className="flex items-center justify-between mt-3">
                            <Badge variant="secondary" className="bg-red-500 text-white">
                              <Heart className="h-3 w-3 mr-1 fill-current" />
                              {spot.likes}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="visited-spots">
              <div className="mt-6">
                {loadingTab ? (
                  <div className="text-center py-8">Chargement...</div>
                ) : profileData.visitedSpots.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune visite enregistrée</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {profileData.visitedSpots.map((spot) => (
                      <Card key={spot.id} className="overflow-hidden cursor-pointer" onClick={() => handleViewSpotDetails(spot)}>
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            {spot.photos && spot.photos.length > 0 && (
                              <div className="w-24 h-24 flex-shrink-0">
                                <img
                                  src={spot.photos[0].url}
                                  alt={spot.title}
                                  className="w-full h-full object-cover rounded"
                                />
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className="font-semibold mb-1">{spot.title}</h3>
                              <p className="text-gray-600 text-sm mb-2">{spot.description}</p>
                              <div className="text-xs text-gray-500">
                                {spot.visit_history?.length || 0} visite{spot.visit_history?.length > 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="wishlists">
              <div className="mt-6">
                {loadingTab ? (
                  <div className="text-center py-8">Chargement...</div>
                ) : profileData.wishlists.length === 0 ? (
                  <div className="text-center py-12">
                    <List className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune liste d'envie créée</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {profileData.wishlists.map((wishlist) => (
                      <Card key={wishlist.id}>
                        <CardContent className="p-4">
                          <h3 className="font-semibold mb-2">{wishlist.name}</h3>
                          {wishlist.description && (
                            <p className="text-gray-600 text-sm mb-3">{wishlist.description}</p>
                          )}
                          <div className="flex items-center justify-between">
                            <Badge variant="outline">
                              {wishlist.spot_ids?.length || 0} spots
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(wishlist.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="contributions">
              <div className="mt-6">
                {loadingTab ? (
                  <div className="text-center py-8">Chargement...</div>
                ) : profileData.contributions.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune contribution pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {profileData.contributions.map((item) => (
                      <Card key={item.contribution.id} className="overflow-hidden cursor-pointer" onClick={() => handleViewSpotDetails(item.spot)}>
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            {item.spot.photos && item.spot.photos.length > 0 && (
                              <div className="w-16 h-16 flex-shrink-0">
                                <img
                                  src={item.spot.photos[0].url}
                                  alt={item.spot.title}
                                  className="w-full h-full object-cover rounded"
                                />
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className="font-semibold mb-1">{item.spot.title}</h3>
                              <p className="text-gray-600 text-sm mb-2">{item.spot.description}</p>
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>{item.spot.likes} likes</span>
                                <span>{new Date(item.contribution.created_at).toLocaleDateString('fr-FR')}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="liked-photos">
              <div className="mt-6">
                {loadingTab ? (
                  <div className="text-center py-8">Chargement...</div>
                ) : profileData.likedPhotos.length === 0 ? (
                  <div className="text-center py-12">
                    <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune photo likée</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {profileData.likedPhotos.map((photoLike) => (
                      <div key={photoLike.id} className="aspect-square">
                        <img
                          src={photoLike.photo_url}
                          alt="Photo likée"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Spot Details Modal */}
        {state.showSpotDetails && state.selectedSpot && (
          <SpotDetailsModal
            onClose={() => actions.setShowSpotDetails(false)}
          />
        )}
      </div>
    </div>
  );
}